import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getDictionary } from '../requests';
import type { DictionaryResponse } from '@/shared/types/dictionaryTypes';
import { CACHE_TIME_1_DAY, CACHE_TIME_1_WEEK } from '@/shared/config';
import { secondsToMilliseconds } from '@/shared/utils/datetime';
import { useAppSettingsStore } from '@/shared/lib';

export type UseDictionaryOptions<TDictionaryValue = Record<string, unknown>> = {
  dictionaryName: string;
  dictionaryVersion?: string;
  language?: string;
  queryKey?: string[];
} & Omit<
  UseQueryOptions<
    DictionaryResponse<TDictionaryValue>,
    Error,
    DictionaryResponse<TDictionaryValue>
  >,
  'queryKey' | 'queryFn'
>;

export function useDictionary<TDictionaryValue = Record<string, unknown>>({
  dictionaryName,
  dictionaryVersion,
  enabled = true,
  gcTime = CACHE_TIME_1_WEEK,
  language,
  queryKey,
  ...options
}: UseDictionaryOptions<TDictionaryValue>) {
  const { getLanguage } = useAppSettingsStore();
  const locale = getLanguage();

  const queryKeys = [
    'dictionary',
    dictionaryName,
    dictionaryVersion,
    language ?? locale,
    ...(Array.isArray(queryKey) ? queryKey : []),
  ];

  const query = useQuery<DictionaryResponse<TDictionaryValue>, Error>({
    queryKey: queryKeys,
    queryFn: async () => {
      return await getDictionary<TDictionaryValue>(dictionaryName);
    },
    enabled: enabled && !!dictionaryName,
    staleTime: ({ state: { data } }) =>
      secondsToMilliseconds(data?.timeout || 0) || CACHE_TIME_1_DAY,
    gcTime,
    retry: 1,
    meta: { persist: true },
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    status: query.status,
    refetch: query.refetch,
    query,
  };
}
